import { useState } from "react";

import {
  Button,
  Card,
  Flex,
  HStack,
  Heading,
  InfoBox,
  RefreshButton,
  Text,
  VStack,
} from "components/ui";
import { useAiGateway } from "hooks/useAiGateway";
import { useTranslation } from "hooks/useTranslation";
import { getThemeColor } from "theme/colors";

import ConfigChangesPanel from "./ConfigChangesPanel";
import ConnectorsPanel from "./ConnectorsPanel";
import DifficultyPanel from "./DifficultyPanel";
import EffectiveConfigCard from "./EffectiveConfigCard";
import RoutingPanel from "./RoutingPanel";

type GatewayTab = "connectors" | "routing" | "difficulty" | "effective";

const TABS: GatewayTab[] = ["connectors", "routing", "difficulty", "effective"];

/**
 * AI Gateway — the tenant's view of which model answers which part of a conversation. Pure
 * configuration, behind the `aiGateway` feature: connectors, routing, difficulty profile and the
 * effective setup with its change history.
 *
 * What the gateway *does* is reported elsewhere, for every tenant whether or not the feature is on:
 * spend and usage attribution on Dashboard → AI Spend, budgets on Configure → Budgets & limits, and
 * the per-turn activity as a tab of Analytics → AI actions. The configuration tabs share one load
 * because they are not independent of each other; the change history loads on its own and re-reads
 * on `refreshToken`.
 */
const AiGatewayPage = () => {
  const { t } = useTranslation();
  const [tab, setTab] = useState<GatewayTab>("connectors");
  const [refreshToken, setRefreshToken] = useState(0);
  const {
    connectors,
    health,
    models,
    routes,
    profile,
    overview,
    loading,
    error,
    reload,
  } = useAiGateway();

  /** One button, every tab — the change history owns its own data and re-reads on the token. */
  const refreshAll = () => {
    setRefreshToken((token) => token + 1);
    reload();
  };

  return (
    <VStack align="stretch" gap={5} w="full">
      <Flex justify="space-between" align="flex-start" wrap="wrap" gap={3}>
        <VStack align="start" gap={1}>
          <Heading size="lg">{t("app.aiGateway.title")}</Heading>
          <Text color={getThemeColor("text.secondary")}>
            {t("app.aiGateway.subtitle")}
          </Text>
        </VStack>
        <RefreshButton onClick={refreshAll} loading={loading} iconOnly />
      </Flex>

      {error && (
        <InfoBox variant="error">
          <VStack align="start" gap={2}>
            <Text fontSize="sm">{t("app.aiGateway.loadFailed")}</Text>
            <Button intent="secondary" size="sm" onClick={reload}>
              {t("app.aiGateway.retry")}
            </Button>
          </VStack>
        </InfoBox>
      )}

      <HStack gap={2} wrap="wrap">
        {TABS.map((name) => (
          <Button
            key={name}
            intent={tab === name ? "primary" : "secondary"}
            size="sm"
            onClick={() => setTab(name)}
          >
            {t(`app.aiGateway.tabs.${name}`)}
          </Button>
        ))}
      </HStack>

      <Card.Root w="full">
        <Card.Body>
          {tab === "connectors" && (
            <ConnectorsPanel
              connectors={connectors}
              health={health}
              loading={loading}
              onChanged={reload}
            />
          )}
          {tab === "routing" && (
            <RoutingPanel
              routes={routes}
              models={models}
              loading={loading}
              onChanged={reload}
            />
          )}
          {tab === "difficulty" && (
            <DifficultyPanel
              profile={profile}
              models={models}
              loading={loading}
              onChanged={reload}
            />
          )}
          {tab === "effective" && (
            <VStack align="stretch" gap={6}>
              <EffectiveConfigCard overview={overview} loading={loading} />
              <ConfigChangesPanel refreshToken={refreshToken} />
            </VStack>
          )}
        </Card.Body>
      </Card.Root>
    </VStack>
  );
};

export default AiGatewayPage;
